import { Table, TableHead, TableRow, TableHeaderCell, TableCell } from './ui/Table'
import UiInput from './ui/Input'
import Button from './ui/Button'
import React, { useMemo, useState } from "react";
import { labourRateBandKeys } from "../common";
import { workerTypes } from "../constants";
import ModalComponent from "./Modal";

const workerCounts = Array.from({ length: 10 }, (_, i) => i + 1);

const bandLabels = {
    weekdayMorningsRate: "Monday - Friday (6AM - 6PM)",
    weekdayNightsRate: "Monday - Friday (6PM - 6AM)",
    fridayNightRate: "Friday (6PM - 6AM)",
    saturdayDayRate: "Saturday (6AM - 6PM)",
    saturdayNightRate: "Saturday (6PM - 6AM)",
    sundayDayRate: "Sunday (6AM - 6PM)",
    sundayNightRate: "Sunday to Monday (6PM - 6AM)",
};

const moreWorkerCounts = workerCounts.slice(1);

export default function CompanyRateTable({
    labourRates,
    setLabourRates,
    editing = false,
}) {
    const [activeWorkerType, setActiveWorkerType] = useState("");

    const modalTitle = useMemo(
        () => activeWorkerType ? `${activeWorkerType} - Additional Worker Rates` : "",
        [activeWorkerType]
    );

    const updateRate = (band, workerType, index, value) => {
        setLabourRates((prev) => ({
            ...prev,
            [band]: {
                ...(prev?.[band] || {}),
                [workerType]: Array.from({ length: 10 }, (_, rateIndex) => (
                    rateIndex === index
                        ? value
                        : (prev?.[band]?.[workerType]?.[rateIndex] ?? "")
                )),
            },
        }));
    };

    const renderCurrencyInput = ({ band, workerType, index, ariaLabel }) => (
        <div className={`clientRateCurrencyInput ${editing ? "" : "clientRateCurrencyInputDisabled"}`}>
            <span aria-hidden="true">$</span>
            <UiInput
                type="text"
                inputMode="decimal"
                aria-label={ariaLabel}
                value={labourRates?.[band]?.[workerType]?.[index] ?? ""}
                onChange={(e) => updateRate(band, workerType, index, e.target.value)}
                disabled={!editing}
                placeholder="0"
            />
        </div>
    );

    return (
        <>
            <div className="clientRatesSummaryTableWrap">
                <div className="clientWorkerRatesToolbar">
                    <p className="clientWorkerRatesTitle">Worker Rates</p>
                    <p className="clientWorkerRatesCaption">Single worker rates shown below.</p>
                </div>
                <div className="overflow-x-auto clientRatesSummaryScroller">
                    <Table emptyItems="rates" className="clientRatesSummaryTable">
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell aria-label="Rate period" />
                                {workerTypes.map((type) => (
                                    <TableHeaderCell key={type}>
                                        <div className="clientRatesWorkerHeader">
                                            <span>{type}</span>
                                        </div>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <tbody>
                            {labourRateBandKeys.map((band) => (
                                <TableRow key={band}>
                                    <TableCell className="clientRatesBandCell">{bandLabels[band] || band}</TableCell>
                                    {workerTypes.map((type) => (
                                        <TableCell key={`${band}-${type}`}>
                                            {renderCurrencyInput({
                                                band,
                                                workerType: type,
                                                index: 0,
                                                ariaLabel: `${bandLabels[band] || band}, ${type}, 1 worker`,
                                            })}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            <TableRow className="clientRatesMoreWorkersRow">
                                <TableCell aria-hidden="true" />
                                {workerTypes.map((type) => (
                                    <TableCell key={`more-workers-${type}`}>
                                        <Button variant="secondary" size="sm"
                                            type="button"
                                            className="clientRatesMoreWorkersButton"
                                            onClick={() => setActiveWorkerType(type)}
                                        >
                                            More Workers
                                        </Button>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </tbody>
                    </Table>
                </div>
            </div>
            <ModalComponent
                showModal={!!activeWorkerType}
                setShowModal={() => setActiveWorkerType("")}
                customMaxWidth="1180px"
                customContentStyle={{
                    width: "min(1180px, calc(100vw - 48px))",
                    padding: "0",
                    overflow: "hidden",
                }}
            >
                <div className="clientRatesModal">
                    <div className="clientRatesModalHeader">
                        <div>
                            <p className="clientRatesModalEyebrow">Additional Worker Rates</p>
                            <h2>{modalTitle}</h2>
                        </div>
                        <Button variant="secondary" size="md"
                            type="button"
                            className="clientRatesModalClose"
                            onClick={() => setActiveWorkerType("")}
                            aria-label="Close additional worker rates"
                        >Cancel</Button>
                    </div>
                    <div className="clientRatesModalBody">
                        <div className="overflow-x-auto clientRatesSummaryScroller">
                            <Table emptyItems="rates" className="clientRatesSummaryTable clientRatesMultiWorkerTable">
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell>Time Slot</TableHeaderCell>
                                        {moreWorkerCounts.map((count) => (
                                            <TableHeaderCell key={count}>{count} Workers</TableHeaderCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {labourRateBandKeys.map((band) => (
                                        <TableRow key={`${activeWorkerType}-${band}`}>
                                            <TableCell className="clientRatesBandCell">{bandLabels[band] || band}</TableCell>
                                            {moreWorkerCounts.map((count, offset) => (
                                                <TableCell key={`${activeWorkerType}-${band}-${count}`}>
                                                    {renderCurrencyInput({
                                                        band,
                                                        workerType: activeWorkerType,
                                                        index: offset + 1,
                                                        ariaLabel: `${bandLabels[band] || band}, ${activeWorkerType}, ${count} workers`,
                                                    })}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                </div>
            </ModalComponent>
        </>
    );
}
